import { useEffect, useRef } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { CheckIcon, CircleDotIcon, FileTextIcon, TagIcon, TicketIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import { getProjectMembers } from "@/features/projects/services/project";
import { AssigneeSelector } from "@/features/tickets/components/AssigneeSelector";
import { DiscardChangesDialog } from "@/features/tickets/components/DiscardChangesDialog";
import { EffortSelector } from "@/features/tickets/components/EffortSelector";
import { PrioritySelector } from "@/features/tickets/components/PrioritySelector";
import { TagSelector } from "@/features/tickets/components/TagSelector";
import { TypeSelector } from "@/features/tickets/components/TypeSelector";
import { ENVIRONMENTS, ENV_COLOURS, PRIORITY_CONFIG, TYPE_CONFIG } from "@/features/tickets/config";
import { useDiscardGuard } from "@/features/tickets/hooks/useDiscardGuard";
import { CreateTicketSchema, createTicketSchema } from "@/features/tickets/schemas";
import { createTicket } from "@/features/tickets/services/ticket";
import Error from "@/shared/components/Error";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

export default function CreateTicketForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<CreateTicketSchema>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      projectId: id!,
      title: "",
      type: "Bug",
      priority: "Medium",
      description: "",
      assignedToId: undefined,
      tags: [],
      effort: 3,
      reproducingSteps: "",
      expectedBehaviour: "",
      actualBehaviour: "",
      affectedVersion: "",
      environment: undefined,
    },
  });

  const { discardOpen, setDiscardOpen, handleDiscard } = useDiscardGuard(form, `/projects/${id}/tickets`);

  const mutation = useMutation<string, AppError | Error, CreateTicketSchema>({
    mutationFn: createTicket,
    onSuccess: (ticketId) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_TICKETS, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_TAGS, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_BY_ID, id, ticketId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, id] });
      navigate(`/projects/${id}/tickets/${ticketId}`);
    },
  });

  useEffect(() => {
    if (mutation.isError) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [mutation.isError]);

  const {
    data: membersData,
    isPending: isMembersPending,
    isError: isMembersError,
    error: membersError,
    fetchNextPage: fetchNextMembersPage,
    isFetchingNextPage: isFetchingNextMembersPage,
    hasNextPage: hasNextMembersPage,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_PROJECT_MEMBERS, id],
    queryFn: ({ pageParam }) => getProjectMembers(id!, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
  });

  useEffect(() => {
    if (hasNextMembersPage && !isFetchingNextMembersPage) {
      fetchNextMembersPage();
    }
  }, [hasNextMembersPage, isFetchingNextMembersPage, fetchNextMembersPage]);

  const isLoadingMembers = isMembersPending || hasNextMembersPage || isFetchingNextMembersPage;

  const members = (membersData?.pages.flatMap((page) => page.members) ?? []).map((member) => ({
    id: member.id,
    memberId: member.id,
    name: `${member.firstName} ${member.lastName}`,
    initials: `${member.firstName[0]}${member.lastName[0]}`.toUpperCase(),
    role: member.role.name,
    avatarUrl: "",
  }));

  const watchedType = form.watch("type");
  const watchedPriority = form.watch("priority");
  const watchedTitle = form.watch("title");
  const watchedEffort = form.watch("effort") ?? 3;
  const watchedAssignedToId = form.watch("assignedToId");
  const watchedTags = form.watch("tags");
  const watchedEnvironment = form.watch("environment");

  const isBugType = watchedType === "Bug";
  const assignedMember = members.find((m) => m.id === watchedAssignedToId) ?? null;

  function toggleTag(tag: string) {
    const current = form.getValues("tags");
    form.setValue("tags", current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag]);
  }

  async function onSubmit(values: CreateTicketSchema) {
    mutation.mutate(values);
  }

  return (
    <FormProvider {...form}>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          {/* Page header */}
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-normal uppercase">New ticket</h2>
              <p className="text-sm text-muted-foreground">Fill in the details below to raise a ticket against this project.</p>
            </div>
            <div className="hidden sm:flex sm:flex-row items-center gap-2">
              <Button variant="outline" size="sm" type="button" disabled={mutation.isPending || mutation.isSuccess} className={mutation.isPending || mutation.isSuccess ? "pointer-events-none opacity-50" : ""} onClick={handleDiscard}>
                Discard
              </Button>
              <Button className="w-full sm:w-auto" size="sm" type="submit" disabled={mutation.isPending || mutation.isSuccess}>
                <TicketIcon />
                {mutation.isPending || mutation.isSuccess ? "Creating..." : "Create ticket"}
              </Button>
            </div>
          </div>

          {mutation.isError && <Error title="Unable to create ticket" error={mutation.error} />}

          {/* Body: two-column layout */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
            {/* ── Left column ── */}
            <div className="flex flex-col gap-6">
              <Card>
                <CardContent className="pt-6">
                  <FieldGroup>
                    <Controller
                      name="projectId"
                      control={form.control}
                      render={({ field }) => (
                        <Field>
                          <Input type="hidden" {...field} />
                        </Field>
                      )}
                    />
                    <Controller
                      name="title"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={field.name} className="text-xs font-semibold uppercase tracking-wider">
                            Title <span className="text-destructive">*</span>
                          </FieldLabel>
                          <Input {...field} id={field.name} placeholder="A concise, descriptive title for this ticket…" disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} autoComplete="off" />
                          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      )}
                    />
                  </FieldGroup>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Ticket type</CardTitle>
                  <CardDescription>What kind of work does this ticket represent?</CardDescription>
                </CardHeader>
                <CardContent>
                  <Controller name="type" control={form.control} render={({ field }) => <TypeSelector value={field.value} onChange={field.onChange} disabled={mutation.isPending || mutation.isSuccess} />} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Description</CardTitle>
                  <CardDescription>{isBugType ? "Document the bug with steps to reproduce, expected behaviour, and what actually happened." : "Describe what needs to be done and why."}</CardDescription>
                </CardHeader>
                <CardContent>
                  {isBugType ? (
                    <Tabs defaultValue="description">
                      <TabsList className="mb-4 h-full w-full flex-col gap-1 bg-muted p-1 sm:flex-row">
                        <TabsTrigger value="description" disabled={mutation.isPending || mutation.isSuccess} className="w-full justify-center data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                          Overview
                        </TabsTrigger>
                        <TabsTrigger value="steps" disabled={mutation.isPending || mutation.isSuccess} className="w-full justify-center data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                          Steps to reproduce
                        </TabsTrigger>
                        <TabsTrigger value="expected" disabled={mutation.isPending || mutation.isSuccess} className="w-full justify-center data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none">
                          Expected vs. actual
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="description">
                        <Controller
                          name="description"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <Field data-invalid={fieldState.invalid}>
                              <Textarea {...field} placeholder="Briefly describe the bug and its impact…" disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} className="min-h-32" />
                              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                          )}
                        />
                      </TabsContent>
                      <TabsContent value="steps">
                        <Controller
                          name="reproducingSteps"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <Field data-invalid={fieldState.invalid}>
                              <Textarea {...field} placeholder={`1. Navigate to…\n2. Click on…\n3. Observe that…`} rows={10} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} className="min-h-40" />
                              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                          )}
                        />
                      </TabsContent>
                      <TabsContent value="expected">
                        <div className="flex flex-col gap-4">
                          <Controller
                            name="expectedBehaviour"
                            control={form.control}
                            render={({ field, fieldState }) => (
                              <Field data-invalid={fieldState.invalid}>
                                <FieldLabel className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">Expected behaviour</FieldLabel>
                                <Textarea {...field} placeholder="What should happen?" disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} className="min-h-24" />
                                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                              </Field>
                            )}
                          />
                          <Separator />
                          <Controller
                            name="actualBehaviour"
                            control={form.control}
                            render={({ field, fieldState }) => (
                              <Field data-invalid={fieldState.invalid}>
                                <FieldLabel className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">Actual behaviour</FieldLabel>
                                <Textarea {...field} placeholder="What actually happens?" disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} className="min-h-24" />
                                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                              </Field>
                            )}
                          />
                        </div>
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <Controller
                      name="description"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <Textarea {...field} placeholder="Describe the feature, task, or improvement in detail…" disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} className="min-h-40" />
                          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      )}
                    />
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Priority</CardTitle>
                  <CardDescription>How urgently does this ticket need to be resolved?</CardDescription>
                </CardHeader>
                <CardContent>
                  <Controller name="priority" control={form.control} render={({ field }) => <PrioritySelector value={field.value} onChange={field.onChange} disabled={mutation.isPending || mutation.isSuccess} />} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Assignee</CardTitle>
                  <CardDescription>Who is responsible for this ticket? Select a team member, or leave unassigned.</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  {isLoadingMembers ? (
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-12 w-36" />
                      ))}
                    </div>
                  ) : isMembersError ? (
                    <Error title="Failed to load members" error={membersError} />
                  ) : (
                    <Controller name="assignedToId" control={form.control} render={({ field }) => <AssigneeSelector members={members} selected={field.value ?? null} onChange={(memberId) => field.onChange(memberId)} disabled={mutation.isPending || mutation.isSuccess} />} />
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Tags</CardTitle>
                  <CardDescription>Label this ticket to make filtering and searching easier.</CardDescription>
                </CardHeader>
                <CardContent>
                  <TagSelector selected={watchedTags} onToggle={toggleTag} disabled={mutation.isPending || mutation.isSuccess} />
                </CardContent>
              </Card>
            </div>

            {/* ── Right column (sidebar) ── */}
            <div className="flex flex-col gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Effort estimate</CardTitle>
                  <CardDescription>Story points using Fibonacci scale.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Controller name="effort" control={form.control} render={({ field }) => <EffortSelector value={field.value ?? 3} onChange={field.onChange} disabled={mutation.isPending || mutation.isSuccess} />} />
                </CardContent>
              </Card>

              {isBugType && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Environment</CardTitle>
                    <CardDescription>Where was this bug observed?</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Controller
                      name="environment"
                      control={form.control}
                      render={({ field }) => (
                        <div className="flex flex-col gap-1">
                          {ENVIRONMENTS.map((env) => {
                            const selected = field.value === env;
                            return (
                              <button
                                key={env}
                                type="button"
                                onClick={() => field.onChange(selected ? undefined : env)}
                                disabled={mutation.isPending || mutation.isSuccess}
                                className={`flex items-center justify-between border px-3 py-2 text-left transition-colors
                                  ${selected ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-accent"}
                                  ${mutation.isPending || mutation.isSuccess ? "pointer-events-none opacity-50" : ""}`}
                              >
                                <span className={`text-xs font-semibold ${selected ? ENV_COLOURS[env] : "text-muted-foreground"}`}>{env}</span>
                                {selected && <CheckIcon className="size-3 text-primary" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    />
                  </CardContent>
                </Card>
              )}

              {isBugType && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Affected version</CardTitle>
                    <CardDescription>Build or version number where the bug occurs.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Controller
                      name="affectedVersion"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <Input {...field} placeholder="e.g. v1.4.2, build 20250501" disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} className="text-xs font-mono" />
                          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      )}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Live preview summary */}
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <FileTextIcon className="size-3.5 text-primary" />
                    <CardTitle className="text-sm text-primary">Preview</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <SectionHeading>Title</SectionHeading>
                    <p className="text-sm font-medium leading-snug">{watchedTitle || <span className="italic text-muted-foreground">No title yet…</span>}</p>
                  </div>
                  <Separator />
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const cfg = TYPE_CONFIG[watchedType];
                      const Icon = cfg.icon;
                      return (
                        <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${cfg.className}`}>
                          <Icon className="size-3" />
                          {watchedType}
                        </span>
                      );
                    })()}
                    {(() => {
                      const cfg = PRIORITY_CONFIG[watchedPriority];
                      const Icon = cfg.icon;
                      return (
                        <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${cfg.className}`}>
                          <Icon className="size-3" />
                          {watchedPriority}
                        </span>
                      );
                    })()}
                    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      <CircleDotIcon className="size-3" />
                      {watchedEffort} pts
                    </span>
                  </div>
                  {assignedMember && (
                    <>
                      <Separator />
                      <div className="flex flex-col gap-1.5">
                        <SectionHeading>Assignee</SectionHeading>
                        <div className="flex items-center gap-2">
                          <Avatar size="sm">
                            <AvatarImage src={assignedMember.avatarUrl} alt={assignedMember.name} />
                            <AvatarFallback className="text-[9px]">{assignedMember.initials}</AvatarFallback>
                          </Avatar>
                          <span className="text-xs font-medium">{assignedMember.name}</span>
                        </div>
                      </div>
                    </>
                  )}
                  {watchedEnvironment && (
                    <>
                      <Separator />
                      <div className="flex flex-col gap-1.5">
                        <SectionHeading>Environment</SectionHeading>
                        <span className={`text-xs font-semibold ${ENV_COLOURS[watchedEnvironment]}`}>{watchedEnvironment}</span>
                      </div>
                    </>
                  )}
                  {watchedTags.length > 0 && (
                    <>
                      <Separator />
                      <div className="flex flex-col gap-1.5">
                        <SectionHeading>Tags</SectionHeading>
                        <div className="flex flex-wrap gap-1">
                          {watchedTags.map((tag) => (
                            <span key={tag} className="flex items-center gap-1 border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                              <TagIcon className="size-2.5 shrink-0" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <div className="flex flex-col gap-2 pt-2">
                <Button size="sm" className="w-full gap-2" type="submit" disabled={mutation.isPending || mutation.isSuccess}>
                  <TicketIcon className="size-4" />
                  {mutation.isPending || mutation.isSuccess ? "Creating..." : "Create ticket"}
                </Button>
                <Button variant="outline" size="sm" className={`w-full ${mutation.isPending || mutation.isSuccess ? "pointer-events-none opacity-50" : ""}`} type="button" disabled={mutation.isPending || mutation.isSuccess} onClick={handleDiscard}>
                  Discard & go back
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DiscardChangesDialog open={discardOpen} onOpenChange={setDiscardOpen} backPath={`/projects/${id}/tickets`} />
      </form>
    </FormProvider>
  );
}
